import { useEffect, useState } from "react";
import api from "../api";
import { collections, curatedPhoto, type Collection } from "../toolRegistry";

/**
 * Overlays AGOL profile data onto the curated collections.
 *
 * The toolset membership is curated in code, but the *person* — their name and
 * photo — comes from AGOL, so this never drifts from the org directory when
 * someone changes their name or uploads a picture.
 *
 * Degrades quietly: if the request fails the hardcoded names are used, because
 * a missing photo is not a reason to break the tools page.
 */

type OrgUser = { username: string; fullName: string; hasPhoto: boolean };

// Module-level cache: several pages render collections, and none of them need
// to re-fetch a directory that changes a few times a year.
let cache: Record<string, OrgUser> | null = null;
let inflight: Promise<Record<string, OrgUser>> | null = null;

async function fetchProfiles(): Promise<Record<string, OrgUser>> {
  if (cache) return cache;
  if (!inflight) {
    inflight = api
      .get("/org-users")
      .then((res) => {
        cache = (res.data?.users ?? {}) as Record<string, OrgUser>;
        return cache;
      })
      .catch(() => ({} as Record<string, OrgUser>))
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

function initialsOf(fullName: string, fallback: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Collections with AGOL name, initials and photo applied where available. */
export function useOrgCollections(): Collection[] {
  const [profiles, setProfiles] = useState<Record<string, OrgUser> | null>(cache);

  useEffect(() => {
    let alive = true;
    fetchProfiles().then((p) => {
      if (alive) setProfiles(p);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!profiles) return collections;

  return collections.map((c) => {
    const u = profiles[c.agolUsername];
    if (!u) return c;
    return {
      ...c,
      person: u.fullName,
      initials: initialsOf(u.fullName, c.initials),
      // A curated photo outranks the directory. Without this the AGOL overlay
      // would overwrite the chosen picture as soon as /org-users resolved, so
      // the right image would appear and then visibly swap itself out.
      //
      // The AGOL URL must carry the API base: in production the frontend is
      // served at / and the backend behind /api, so a bare path would hit the
      // SPA fallback and return index.html instead of an image.
      photo:
        curatedPhoto(c.id) ??
        (u.hasPhoto
          ? `${api.defaults.baseURL ?? ""}/org-user-photo/${encodeURIComponent(c.agolUsername)}`
          : c.photo),
    };
  });
}
